import React, { CSSProperties, FC, Key, useContext, useEffect, useState } from 'react';
import styles from './index.module.scss';
import classNames from 'classnames';
import { Spin } from 'antd';
import { Input, Empty, Tree, Button, Tooltip } from '@maxtropy/components';
import { apiV2AreaTreePost, V2AreaTreePostResponse } from '@maxtropy/device-customer-apis-v2';

import nodeIcon from '../imgs/menu.png';
import { getAllKeys, NewDataNode } from '../utils';
import { EnergyStaticNoProductContext } from '..';
import { DataNode } from 'antd/es/tree';
import { isNil } from 'lodash-es';
import NoEnergyIcon from '../imgs/disconnect.png';

export interface TreeProps {
  className?: string;
  style?: CSSProperties;
}

const AreaTree: FC<TreeProps> = props => {
  const { areaId, setAreaId, setEnergyUnitId } = useContext(EnergyStaticNoProductContext);
  const { className, style } = props;
  const [searchParams, setSearchParams] = useState<{ name: string }>(); // 搜索
  const [expandedKeys, setExpandedKeys] = useState<number[]>([]);
  const [originData, setOriginData] = useState<NewDataNode[]>([]); // 原始数据
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<NewDataNode[]>();
  const [expandAll, setExpandAll] = useState<boolean>(true);

  // 请求树数据
  useEffect(() => {
    setLoading(true);
    apiV2AreaTreePost({})
      .then(res => {
        const treeData = transformTree(res.list ?? []);
        const allKeys = getAllKeys(treeData);
        setExpandedKeys(allKeys);
        setExpandAll(true);
        setOriginData(treeData);
        setData(treeData);
        if (!treeData.length) {
          setAreaId?.(undefined);
        } else {
          const firstNode = selectDefaultNode(treeData);
          if (firstNode) {
            setAreaId?.(firstNode.key as number);
            setEnergyUnitId?.(isNil(firstNode.energyUnitId) ? undefined : firstNode.energyUnitId);
          } else {
            setAreaId?.(undefined);
            setEnergyUnitId?.(undefined);
          }
        }
      })
      .finally(() => setLoading(false));
  }, []);

  // 前端过滤
  useEffect(() => {
    const searchName = searchParams?.name?.trim();
    if (!searchName) {
      setData(originData);
      setExpandedKeys(getAllKeys(originData));
      return;
    }

    const expandedKeysSet = new Set<number>();

    const filterTree = (nodes: NewDataNode[], parentKeys: number[] = []): NewDataNode[] => {
      return nodes
        .map(node => {
          const rawTitle = node.rawTitle ?? '';
          const match = rawTitle.includes(searchName);

          const children = node.children ? filterTree(node.children, [...parentKeys, node.key as number]) : [];

          if (match || children.length > 0) {
            // 如果当前节点匹配或者子节点匹配，需要展开父节点
            parentKeys.forEach(key => expandedKeysSet.add(key));

            return {
              ...node,
              title: renderNodeTitle(rawTitle, !isNil(node.energyUnitId), searchName),
              children,
            };
          }

          return null;
        })
        .filter(Boolean) as NewDataNode[];
    };

    const filtered = filterTree(originData);
    setData(filtered);
    setExpandedKeys(Array.from(expandedKeysSet)); // 搜索结果自动展开父节点
  }, [searchParams, originData]);

  const findParentKeys = (tree: DataNode[], targetKey: Key, path: Key[] = []): Key[] | null => {
    for (const node of tree) {
      const newPath = [...path, node.key];

      if (node.key === targetKey) {
        return newPath;
      }

      if (node.children) {
        const result = findParentKeys(node.children, targetKey, newPath);
        if (result) return result;
      }
    }
    return null;
  };

  // 搜索树的选择框
  const onSelect = (selectedKeys: Key[], e: any) => {
    if (!selectedKeys.length) return;

    const node = e.node as NewDataNode;

    setAreaId?.(selectedKeys[0] as number);

    setEnergyUnitId?.(isNil(node.energyUnitId) ? undefined : node.energyUnitId);
  };

  return (
    <div className={classNames(className, styles.treeArea)} style={style}>
      <div className={styles.searchArea}>
        <Input.Search
          placeholder="请输入区域名称搜索"
          allowClear
          onSearch={v => {
            setSearchParams({ name: v });
          }}
        />
      </div>

      <div className={styles.treeBox}>
        <Button
          wrapStyle={{ margin: '4px 0 8px 10px' }}
          onClick={() => {
            if (!data) return;

            if (expandAll) {
              setExpandedKeys([]);
            } else {
              setExpandedKeys(getAllKeys(data));
            }

            setExpandAll(!expandAll);
          }}
        >
          {expandAll ? '收起' : '展开'}全部
        </Button>
        <Spin spinning={loading}>
          {data && data.length ? (
            <Tree
              className={styles.treeStyle}
              blockNode
              showIcon
              treeData={data}
              selectedKeys={isNil(areaId) ? [] : [areaId]}
              expandedKeys={expandedKeys}
              onExpand={expandedKeys => {
                setExpandedKeys(expandedKeys as number[]);
              }}
              onSelect={onSelect}
            />
          ) : (
            <Empty style={{ marginTop: 80 }} />
          )}
        </Spin>
      </div>
    </div>
  );
};

export default AreaTree;

// 使得返回的数据变成DataNode格式
export const transformTree = (tree?: V2AreaTreePostResponse['list']): NewDataNode[] => {
  return (tree ?? []).map(item => {
    const hasEnergy = !isNil(item.energyUnitId);

    const node: NewDataNode = {
      key: item.id!,
      energyUnitId: item.energyUnitId,
      title: renderNodeTitle(item.zoneName!, hasEnergy),
      rawTitle: item.zoneName!,
      icon: <img src={nodeIcon} style={{ marginBottom: 4, width: 16, height: 16 }} alt="" />,
    };

    if (item.children?.length) {
      node.children = transformTree(item.children);
    }

    return node;
  });
};

// 寻找第一个key
export function selectDefaultNode(treeData: NewDataNode[]): NewDataNode | undefined {
  for (const node of treeData) {
    if (node.children?.length) {
      const found = selectDefaultNode(node.children);
      if (found) return found;
    }
    return node;
  }
  return undefined;
}

export const renderNodeTitle = (title: string, hasEnergy: boolean, searchValue?: string) => {
  let content: React.ReactNode = title;

  if (searchValue) {
    const index = title.indexOf(searchValue);
    if (index !== -1) {
      const before = title.slice(0, index);
      const match = title.slice(index, index + searchValue.length);
      const after = title.slice(index + searchValue.length);

      content = (
        <>
          {before}
          <span style={{ color: 'red' }}>{match}</span>
          {after}
        </>
      );
    }
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span>{content}</span>

      {!hasEnergy && (
        <Tooltip title="未绑定用能单元">
          <img src={NoEnergyIcon} style={{ width: 16, height: 16 }} title="该节点未绑定用能单元" alt="" />
        </Tooltip>
      )}
    </span>
  );
};
